import {
  CalendarDays,
  Hash,
  ToggleLeft,
  Type,
  type LucideIcon,
} from 'lucide-react';

import { TextWithTooltip } from '../../../components/custom/text-with-tooltip';
import { useApRipple } from '../../../components/theme-provider';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import { useBuilderStateContext } from '../builder-hooks';

import {
  MentionTreeNode,
  formatValuePreview,
  formatValueTooltip,
  triggerClickOnEnterOrSpace,
} from './data-selector-utils';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}|$)/;

// Simplified-view leaves are always scalars (objects/arrays have children and
// are recursed into; empty ones surface as the string 'Empty List'; null/empty
// are filtered out), so we only map number / date / boolean / text.
const getValueIcon = (
  value: unknown,
): { Icon: LucideIcon; className: string } => {
  if (typeof value === 'boolean') {
    return { Icon: ToggleLeft, className: 'text-violet-500' };
  }
  const str = typeof value === 'string' ? value : '';
  if (DATE_PATTERN.test(str)) {
    return { Icon: CalendarDays, className: 'text-teal-500' };
  }
  if (
    typeof value === 'number' ||
    (str.trim() !== '' && !Number.isNaN(Number(str)))
  ) {
    return { Icon: Hash, className: 'text-orange-500' };
  }
  return { Icon: Type, className: 'text-sky-600' };
};

type DataSelectorSimplifiedItemProps = {
  node: MentionTreeNode;
};

const DataSelectorSimplifiedItem = ({
  node,
}: DataSelectorSimplifiedItemProps) => {
  const insertMention = useBuilderStateContext((state) => state.insertMention);
  const [ripple, rippleEvent] = useApRipple();

  const breadcrumbSegments = node.data.breadcrumb?.split(' › ') ?? [];
  const lastKey = breadcrumbSegments[breadcrumbSegments.length - 1] ?? '';
  const ancestors = breadcrumbSegments.slice(0, -1);
  const relativePath = ancestors.join(' › ');
  const isPathCollapsed = ancestors.length > 2;
  const collapsedPath = isPathCollapsed
    ? `${ancestors[0]} › … › ${ancestors[ancestors.length - 1]}`
    : relativePath;

  const valuePreview = formatValuePreview(node.data.value);
  const { Icon: ValueIcon, className: valueIconColor } = getValueIcon(
    node.data.value,
  );

  const insert = (e: React.MouseEvent) => {
    if (insertMention && node.data.insertable) {
      rippleEvent(e);
      insertMention(node.data.propertyPath);
    }
  };

  return (
    <div className="py-1 pl-4 pr-2">
      <div
        role="button"
        tabIndex={0}
        onKeyDown={triggerClickOnEnterOrSpace}
        ref={ripple}
        onClick={insert}
        className={cn(
          'group flex items-center gap-3 rounded-lg border border-border px-2 py-2',
          'cursor-pointer select-none transition-colors focus:outline-none',
          'hover:bg-accent focus:bg-accent',
        )}
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <ValueIcon className={cn('size-4', valueIconColor)} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <TextWithTooltip tooltipMessage={lastKey}>
            <span className="text-sm font-medium text-foreground">
              {lastKey}
            </span>
          </TextWithTooltip>
          {collapsedPath &&
            (isPathCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate text-xs text-muted-foreground/70">
                    {collapsedPath}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-xs break-words text-xs"
                >
                  {relativePath}
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="truncate text-xs text-muted-foreground/70">
                {collapsedPath}
              </span>
            ))}
        </div>

        <TextWithTooltip tooltipMessage={formatValueTooltip(node.data.value)}>
          <span className="max-w-[45%] shrink-0 text-right text-sm font-medium text-primary">
            {valuePreview}
          </span>
        </TextWithTooltip>
      </div>
    </div>
  );
};

DataSelectorSimplifiedItem.displayName = 'DataSelectorSimplifiedItem';
export { DataSelectorSimplifiedItem };
